
import { cropUrl } from '@/utils/images'
import {
  IMAGE_STORY_DURATION,
  INSTAGRAM_PROFILE_URL,
  SWIPE_DISTANCE,
  formatStoryAge,
  hasSeenSwipeHint,
  rememberSwipeHint,
  swipeDirection,
} from '@/utils/instagramStories'

const HOLD_DELAY = 200

const HINT_DURATION = 3500

const ARROWS_HIDDEN = '(max-width: 575.98px)'

export default {
  name: 'InstagramStoriesViewer',
  props: {
    stories: {
      type: Array,
      required: true,
    },
    startIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      index: this.startIndex,
      elapsed: 0,
      lastFrame: null,
      loaded: false,
      held: false,
      wasHeld: false,
      swipeStart: null,
      swiped: false,
      hint: false,
      hidden: false,
      muted: false,
      closing: false,
      holdTimer: null,
      hintTimer: null,
      frame: null,
      profileUrl: INSTAGRAM_PROFILE_URL,
      viewedIds: [],
      completed: false,
    }
  },
  computed: {
    story() {
      return this.stories[this.index] || null
    },
    video() {
      return Boolean(this.story?.video)
    },
    paused() {
      return this.held || this.hidden
    },
    age() {
      return this.story
        ? formatStoryAge(this.story.publishedAt, this.$i18n.locale)
        : ''
    },
    posterUrl() {
      return this.story?.image ? this.imageUrl(this.story) : null
    },
  },
  watch: {
    index() {
      this.show()
    },
    paused(paused) {
      if (paused) {
        this.$refs.video?.pause()
      } else {
        this.play()
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.show()
    this.frame = requestAnimationFrame(this.tick)
    if (window.matchMedia(ARROWS_HIDDEN).matches && !hasSeenSwipeHint()) {
      rememberSwipeHint()
      this.hint = true
      this.hintTimer = setTimeout(() => {
        this.hint = false
      }, HINT_DURATION)
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    cancelAnimationFrame(this.frame)
    clearTimeout(this.holdTimer)
    clearTimeout(this.hintTimer)
  },
  methods: {
    imageUrl(story) {
      return cropUrl(story.image, 720, 0, 'jpg')
    },
    show() {
      this.elapsed = 0
      this.loaded = !this.story
      if (this.story) {
        this.$emit('seen', this.story.id)
        if (!this.viewedIds.includes(this.story.id)) {
          this.viewedIds.push(this.story.id)
          this.$emit('track', 'view')
        }
      } else if (!this.completed) {
        this.completed = true
        this.$emit('track', 'complete')
      }
      this.$nextTick(() => {
        this.paintBars(0)
        this.preloadNext()
      })
    },
    paintBars(progress) {
      Array.from(this.$refs.bars?.children || []).forEach((bar, position) => {
        const fill =
          position < this.index ? 1 : position > this.index ? 0 : progress
        bar.firstElementChild.style.width = `${fill * 100}%`
      })
    },
    preloadNext() {
      const next = this.stories[this.index + 1]
      if (next?.image) {
        new Image().src = this.imageUrl(next)
      }
    },
    async play() {
      const video = this.$refs.video
      if (!video || this.paused) {
        return
      }
      try {
        await video.play()
      } catch (error) {
        if (error?.name !== 'NotAllowedError' || this.muted) {
          return
        }
        this.muted = true
        await this.$nextTick()
        video.play().catch(() => {})
      }
    },
    tick(now) {
      if (
        this.lastFrame !== null &&
        !this.video &&
        this.loaded &&
        !this.paused
      ) {
        this.elapsed += now - this.lastFrame
      }
      this.lastFrame = now

      const video = this.$refs.video
      const progress = this.video
        ? video?.duration
          ? video.currentTime / video.duration
          : 0
        : Math.min(1, this.elapsed / IMAGE_STORY_DURATION)
      this.paintBars(progress)

      if (!this.video && progress >= 1) {
        this.next()
      }
      this.frame = requestAnimationFrame(this.tick)
    },
    next() {
      if (this.index < this.stories.length) {
        this.index += 1
      } else {
        this.hide()
      }
    },
    previous() {
      if (this.index > 0) {
        this.index -= 1
        return
      }
      if (this.$refs.video) {
        this.$refs.video.currentTime = 0
      }
      this.show()
    },
    hide() {
      if (this.closing) {
        return
      }
      this.closing = true
      this.$refs.modal.hide()
    },
    hold(event) {
      this.wasHeld = false
      this.swiped = false
      this.hint = false
      this.swipeStart = { x: event.clientX, y: event.clientY }
      clearTimeout(this.holdTimer)
      this.holdTimer = setTimeout(() => {
        this.held = true
      }, HOLD_DELAY)
    },
    release() {
      this.swipeStart = null
      clearTimeout(this.holdTimer)
      if (this.held) {
        this.held = false
        this.wasHeld = true
      }
    },
    swipe(event) {
      const start = this.swipeStart
      this.release()
      if (!start) {
        return
      }
      const deltaX = event.clientX - start.x
      const deltaY = event.clientY - start.y
      this.swiped = Math.hypot(deltaX, deltaY) >= SWIPE_DISTANCE
      const direction = swipeDirection(deltaX, deltaY)
      if (direction > 0) {
        this.next()
      } else if (direction < 0) {
        this.previous()
      }
    },
    tap(event) {
      if (this.wasHeld || this.swiped) {
        this.wasHeld = false
        this.swiped = false
        return
      }
      const { left, width } = event.currentTarget.getBoundingClientRect()
      if (event.clientX - left < width / 3) {
        this.previous()
      } else {
        this.next()
      }
    },
    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        this.next()
      } else if (event.key === 'ArrowLeft') {
        this.previous()
      }
    },
    onVisibilityChange() {
      this.hidden = document.hidden
      this.lastFrame = null
    },
  },
}
